import React, { useCallback, useEffect, useState } from 'react';
import { ApiErrorCustom, fetchRecordByUuid, fetchRecords, resetApiAccessGuard, updateRecord } from './api';
import { Header } from './components/Header';
import { NewRecordModal } from './components/NewRecordModal';
import { OccConflictModal } from './components/OccConflictModal';
import { RecordDetails } from './components/RecordDetails';
import { RecordList } from './components/RecordList';
import { Toast } from './components/Toast';
import { ServiceRecord, ServiceRecordPatchDto, ServiceRecordStatusFilter } from './types';

export const getInitialSearchQuery = (): string => {
  try {
    return sessionStorage.getItem('crm_search_query') || '';
  } catch (e) {
    return '';
  }
};

export const getInitialStatusFilter = (): ServiceRecordStatusFilter => {
  try {
    const saved = sessionStorage.getItem('crm_status_filter');
    if (
      saved &&
      [
        'ACTIVE',
        'ALL',
        'RECEIVED',
        'DIAGNOSIS',
        'WAITING_FOR_APPROVAL',
        'IN_REPAIR',
        'READY',
        'HANDED_OVER',
      ].includes(saved)
    ) {
      return saved as ServiceRecordStatusFilter;
    }
  } catch (e) {}
  return 'ACTIVE';
};

export const getInitialShowArchived = (): boolean => {
  try {
    return sessionStorage.getItem('crm_show_archived') === 'true';
  } catch (e) {
    return false;
  }
};

export const isUuidFormat = (param: string): boolean => {
  return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(param.trim());
};

export const isTicketNumberFormat = (param: string): boolean => {
  return /^MAD-\d{8}-\d{3}$/i.test(param.trim());
};

export const getUrlRecordParam = (): string | null => {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('record');
  } catch (e) {
    return null;
  }
};

export const updateUrlRecordParam = (recordParam: string | null) => {
  try {
    const url = new URL(window.location.href);
    if (recordParam && recordParam.trim()) {
      url.searchParams.set('record', recordParam.trim());
    } else {
      url.searchParams.delete('record');
    }
    window.history.replaceState({}, '', url.toString());
  } catch (e) {}
};

// Backwards-compatibility wrappers for legacy helper exports
export const getUrlRecordUuid = getUrlRecordParam;
export const updateUrlRecordUuid = updateUrlRecordParam;

export const App: React.FC = () => {
  const [records, setRecords] = useState<ServiceRecord[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<ServiceRecord | null>(null);
  const [searchQuery, setSearchQuery] = useState(getInitialSearchQuery);
  const [statusFilter, setStatusFilter] = useState<ServiceRecordStatusFilter>(getInitialStatusFilter);
  const [showArchived, setShowArchived] = useState<boolean>(getInitialShowArchived);

  const [isLoading, setIsLoading] = useState(true);
  const [isAuthInitializing, setIsAuthInitializing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Mobile navigation state (toggle between list and details on small screens)
  const [isMobileDetailOpen, setIsMobileDetailOpen] = useState(false);

  // OCC Conflict State
  const [isOccConflict, setIsOccConflict] = useState(false);

  // Toast notification
  const [toast, setToast] = useState<{ message: string; type?: 'info' | 'success' | 'error' } | null>(null);

  // New Record Modal State
  const [isNewRecordModalOpen, setIsNewRecordModalOpen] = useState(false);

  const showToast = (message: string, type: 'info' | 'success' | 'error' = 'info') => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 3000);
  };

  const handleRecordCreated = (newRecord: ServiceRecord) => {
    setRecords((prev) => [newRecord, ...prev.filter((r) => r.uuid !== newRecord.uuid)]);
    setSelectedRecord(newRecord);
    setIsMobileDetailOpen(true);
    updateUrlRecordParam(newRecord.ticketNumber);
    showToast(`Utworzono zgłoszenie ${newRecord.ticketNumber}`, 'success');
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    try {
      sessionStorage.setItem('crm_search_query', query);
    } catch (e) {}
  };

  const handleStatusFilterChange = (filter: ServiceRecordStatusFilter) => {
    setStatusFilter(filter);
    try {
      sessionStorage.setItem('crm_status_filter', filter);
    } catch (e) {}
  };

  const handleShowArchivedChange = (show: boolean) => {
    setShowArchived(show);
    try {
      sessionStorage.setItem('crm_show_archived', show ? 'true' : 'false');
    } catch (e) {}
  };

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setErrorMsg(null);
    setIsAuthInitializing(false);
    try {
      const res = await fetchRecords({
        search: searchQuery,
        status: statusFilter,
        showArchived: showArchived,
      });
      setRecords(res.data);

      const recordParam = getUrlRecordParam();

      if (recordParam && recordParam.trim()) {
        const trimmedParam = recordParam.trim();

        if (isUuidFormat(trimmedParam)) {
          // LEGACY UUID RESOLUTION
          const matchInList = res.data.find((r) => r.uuid === trimmedParam);
          if (matchInList) {
            setSelectedRecord(matchInList);
            updateUrlRecordParam(matchInList.ticketNumber);
          } else {
            try {
              const singleRecord = await fetchRecordByUuid(trimmedParam);
              setSelectedRecord(singleRecord);
              updateUrlRecordParam(singleRecord.ticketNumber);
            } catch (e) {
              if (res.data.length > 0) {
                setSelectedRecord(res.data[0]);
                updateUrlRecordParam(res.data[0].ticketNumber);
              } else {
                setSelectedRecord(null);
                updateUrlRecordParam(null);
              }
            }
          }
        } else {
          // CANONICAL TICKET NUMBER RESOLUTION
          const matchInList = res.data.find((r) => r.ticketNumber.toUpperCase() === trimmedParam.toUpperCase());
          if (matchInList) {
            setSelectedRecord(matchInList);
            updateUrlRecordParam(matchInList.ticketNumber);
          } else {
            try {
              const searchRes = await fetchRecords({ search: trimmedParam });
              const singleMatch = searchRes.data.find((r) => r.ticketNumber.toUpperCase() === trimmedParam.toUpperCase());
              if (singleMatch) {
                setSelectedRecord(singleMatch);
                updateUrlRecordParam(singleMatch.ticketNumber);
              } else {
                if (res.data.length > 0) {
                  setSelectedRecord(res.data[0]);
                  updateUrlRecordParam(res.data[0].ticketNumber);
                } else {
                  setSelectedRecord(null);
                  updateUrlRecordParam(null);
                }
              }
            } catch (e) {
              if (res.data.length > 0) {
                setSelectedRecord(res.data[0]);
                updateUrlRecordParam(res.data[0].ticketNumber);
              } else {
                setSelectedRecord(null);
                updateUrlRecordParam(null);
              }
            }
          }
        }
      } else {
        if (res.data.length > 0) {
          setSelectedRecord(res.data[0]);
          updateUrlRecordParam(res.data[0].ticketNumber);
        } else {
          setSelectedRecord(null);
        }
      }
    } catch (err: any) {
      if (err instanceof ApiErrorCustom) {
        if (err.code === 'AUTH_INITIALIZING') {
          setIsAuthInitializing(true);
          setErrorMsg(null);
          return;
        }
        if (err.status === 401 || err.status === 403) {
          setErrorMsg('Brak autoryzacji Cloudflare Access. Zaloguj się do panelu.');
        } else {
          setErrorMsg(`Błąd połączenia z Central API: ${err.message} (${err.code})`);
        }
      } else {
        setErrorMsg('Błąd połączenia z serwerem API.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, statusFilter, showArchived]);

  const handleRetry = useCallback(async () => {
    resetApiAccessGuard();
    await loadData();
  }, [loadData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSelectRecord = (record: ServiceRecord) => {
    setSelectedRecord(record);
    updateUrlRecordParam(record.ticketNumber);
    setIsMobileDetailOpen(true);
  };

  const handleSavePatch = async (uuid: string, patchDto: ServiceRecordPatchDto) => {
    setIsSaving(true);
    try {
      const updatedRecord = await updateRecord(uuid, patchDto);
      setRecords((prev) => prev.map((r) => (r.uuid === uuid ? updatedRecord : r)));
      setSelectedRecord(updatedRecord);
    } catch (err: any) {
      if (err instanceof ApiErrorCustom && err.status === 409) {
        setIsOccConflict(true);
      } else {
        const msg = err instanceof ApiErrorCustom ? err.message : 'Błąd zapisu';
        showToast(`Błąd zapisu: ${msg}`, 'error');
      }
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  const handleOccRefresh = async () => {
    setIsOccConflict(false);
    if (selectedRecord) {
      try {
        const freshRecord = await fetchRecordByUuid(selectedRecord.uuid);
        setSelectedRecord(freshRecord);
        setRecords((prev) => prev.map((r) => (r.uuid === freshRecord.uuid ? freshRecord : r)));
        showToast('Odświeżono aktualne dane z serwera', 'info');
      } catch (e) {
        await loadData();
      }
    } else {
      await loadData();
    }
  };

  return (
    <div className="app-container">
      <Header onRefresh={loadData} onNewRecord={() => setIsNewRecordModalOpen(true)} />

      <main className="main-content">
        <RecordList
          records={records}
          selectedUuid={selectedRecord?.uuid}
          onSelectRecord={handleSelectRecord}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          statusFilter={statusFilter}
          onStatusFilterChange={handleStatusFilterChange}
          showArchived={showArchived}
          onToggleShowArchived={handleShowArchivedChange}
          isLoading={isLoading || isAuthInitializing}
          isMobileDetailViewOpen={isMobileDetailOpen}
          onNewRecord={() => setIsNewRecordModalOpen(true)}
        />

        {isAuthInitializing ? (
          <div className="detail-panel" style={{ justifyContent: 'center', alignItems: 'center' }}>
            <div className="card-container" style={{ textAlign: 'center', maxWidth: '420px', padding: '32px' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>⏳</div>
              <h3 style={{ marginBottom: '8px' }}>MAdKuM CRM</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Łączenie z bezpieczną sesją API...</p>
            </div>
          </div>
        ) : errorMsg ? (
          <div className="detail-panel" style={{ justifyContent: 'center', alignItems: 'center' }}>
            <div className="card-container" style={{ textAlign: 'center', maxWidth: '420px', padding: '32px' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>🔒</div>
              <h3 style={{ marginBottom: '8px' }}>Wymagana autoryzacja</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '16px' }}>{errorMsg}</p>
              <button type="button" className="btn btn-primary" onClick={handleRetry}>
                Spróbuj ponownie
              </button>
            </div>
          </div>
        ) : selectedRecord ? (
          <div className={`detail-wrapper ${!isMobileDetailOpen ? 'hidden-mobile' : ''}`} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <RecordDetails
              record={selectedRecord}
              onSavePatch={handleSavePatch}
              onToast={showToast}
              isSaving={isSaving}
              onBackToList={() => setIsMobileDetailOpen(false)}
            />
          </div>
        ) : (
          <div className="detail-panel">
            <div className="empty-detail-state">
              <div style={{ fontSize: '3rem' }}>📋</div>
              <h3>Wybierz zgłoszenie z listy po lewej stronie</h3>
              <p>Szczegóły zlecenia serwisowego pojawią się w tym panelu.</p>
            </div>
          </div>
        )}
      </main>

      <OccConflictModal
        isOpen={isOccConflict}
        onRefresh={handleOccRefresh}
        onCancel={() => setIsOccConflict(false)}
      />

      <NewRecordModal
        isOpen={isNewRecordModalOpen}
        onClose={() => setIsNewRecordModalOpen(false)}
        onRecordCreated={handleRecordCreated}
      />

      {toast && <Toast message={toast.message} type={toast.type} />}
    </div>
  );
};
